import { FC, useCallback, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Dropdown, Form } from "capitalrx-components-2.0/lib/forms";
import { Modal, useAlert } from "capitalrx-components-2.0/lib/general";
import { Grid, Shelf, Stack, useResponsive } from "capitalrx-components-2.0/lib/layout";
import { Body } from "capitalrx-components-2.0/lib/typography";

import { useUpdateUserPreferencesMutation } from "@/App/api/preferences";
import { ThemedButton, ThemedCheckbox } from "@/App/components";

type Language = "en" | "es";

interface SelectLanguageForm {
  language: Language;
  makePreferred: boolean;
}

export interface LanguageModalProps {
  readonly visible: boolean;
  readonly onSubmit: () => void;
}

export const LanguageModal: FC<LanguageModalProps> = ({ visible, onSubmit }) => {
  const { i18n, t: translate } = useTranslation();

  const [textLng, setTextLng] = useState<Language>(i18n.language as Language);

  const { mutateAsync: updateAppUserPreferences } = useUpdateUserPreferencesMutation();

  const alert = useAlert();

  const { languageFieldCols, modalSize } = useResponsive({
    languageFieldCols: {
      mobile: 12,
      tablet: 12,
      desktop: 6,
    },
    modalSize: {
      mobile: "narrow",
      tablet: "wide",
      desktop: "wide",
    },
  });

  const selectLanguageForm = useForm<SelectLanguageForm>({
    defaultValues: {
      language: null,
      makePreferred: true,
    },
  });

  const lngFixed = i18n.getFixedT(textLng);

  const handleChangeLanguageClick = () => {
    const languages = { en: "es", es: "en" };
    setTextLng(languages[textLng] as Language);
  };

  const handleConfirmButtonClick = () => {
    selectLanguageForm.handleSubmit(onFormSubmit)();
  };

  const onFormSubmit = useCallback(async (formData: SelectLanguageForm) => {
    if (i18n.language !== formData.language) {
      i18n.changeLanguage(formData.language);
    }
    onSubmit();
    if (formData.makePreferred) {
      try {
        await updateAppUserPreferences({ language: formData.language });
        alert({
          type: "success",
          style: "toast",
          message: translate("DASHBOARD.languageModal.languageSettingsUpdateSuccess"),
        });
      } catch {
        alert({
          type: "error",
          style: "toast",
          message: translate("DASHBOARD.languageModal.languageSettingsUpdateError"),
        });
      }
    }
  }, []);

  return (
    <Modal
      size={modalSize as "narrow" | "wide"}
      testId="select-language-modal"
      className="language-modal"
      title={lngFixed("DASHBOARD.languageModal.selectYourLanguage")}
      visible={visible}
      footer={
        <Shelf>
          <ThemedButton appearance="text" onClick={handleChangeLanguageClick} style={{ textTransform: "none" }}>
            {lngFixed("DASHBOARD.languageModal.seeInOtherLanguage")}
          </ThemedButton>
          <Shelf.Spacer />
          <ThemedButton appearance="primary" onClick={handleConfirmButtonClick} testId="language-modal-confirm">
            {lngFixed("DASHBOARD.languageModal.confirm")}
          </ThemedButton>
        </Shelf>
      }
    >
      <Form form={selectLanguageForm} onSubmit={onFormSubmit}>
        <Stack>
          <Body>{lngFixed("DASHBOARD.languageModal.welcomeAndSelectLanguage")}</Body>
          <Grid>
            <Grid.Span columns={languageFieldCols}>
              <Dropdown.Field
                label={lngFixed("DASHBOARD.languageModal.language")}
                placeholder={lngFixed("DASHBOARD.languageModal.select")}
                name="language"
                required={true}
                testId="language-modal-language-select"
              >
                <Dropdown.Option value="en">English</Dropdown.Option>
                <Dropdown.Option value="es">Español</Dropdown.Option>
              </Dropdown.Field>
            </Grid.Span>
          </Grid>
          <ThemedCheckbox.Field
            name="makePreferred"
            label={lngFixed("DASHBOARD.languageModal.makeThisMyPreferredLanguage")}
            hideLabel
            instructions={lngFixed("DASHBOARD.languageModal.thisWillBeDisplayedEveryTime")}
          />
        </Stack>
      </Form>
    </Modal>
  );
};
